<script setup lang="ts">
import { api } from '@/api'
import type Webinar from '@/api/resources/webinars/model'
import { ElMessage } from 'element-plus'
import { computed, ref, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { formatToHumanReadable } from '@/utils'
import { Check, Close, Timer } from '@element-plus/icons-vue'

const router = useRouter()
const route = useRoute()
const id = computed(() => route.query.webinar as string)
const webinar = ref<Webinar>()
const message = ref<string>()

const sendMessage = async () => {
  if (!message.value) {
    return
  }

  await api.broadcastMessage({
    urlParams: {
      id: webinar.value!.id,
    },
    payload: {
      message: message.value,
    },
  })

  ElMessage.success('Message sent')
  message.value = ''
}
const init = async () => {
  if (!id.value) return
  webinar.value = await api.webinars.get(id.value)
}

const deleteWebinar = async () => {
  await api.webinars.delete(id.value)
  ElMessage.success('Webinar deleted')
  const query = { ...route.query }
  delete query.webinar
  router.push({ query })
}

const saveWebinar = async () => {
  await api.webinars.update(id.value, {
    date: webinar.value?.date,
    code: Number(webinar.value?.code),
    language: webinar.value?.language,
    link: webinar.value?.link,
  })

  ElMessage.success('Webinar saved')
  init()
}

watch(
  () => route.query.webinar,
  () => {
    if (route.query.webinar) {
      init()
    } else {
      webinar.value = undefined
    }
  },
  { immediate: true },
)

const notificationsStats = computed(() => {
  return {
    sent: webinar.value?.webinar_notifications.filter((item) => item.is_sent).length,
    failed: webinar.value?.webinar_notifications.filter((item) => item.is_failed).length,
    total: webinar.value?.webinar_notifications.length || 0,
  }
})
</script>

<template>
  <el-card v-if="webinar" shadow="never">
    <el-form-item>
      <el-space>
        <el-date-picker
          v-model="webinar.date"
          type="datetime"
          value-format="YYYY-MM-DDTHH:mm:ss.SSS[Z]"
        />
        {{ webinar.date }}
        <el-input v-model="webinar.code" type="number" />
      </el-space>
    </el-form-item>
    <el-form-item>
      <el-radio-group v-model="webinar.language" :options="['ru', 'en', 'uk']">
        <el-radio-button label="Русский" value="ru" />
        <el-radio-button label="English" value="en" />
        <el-radio-button label="Українська" value="uk" />
      </el-radio-group>
    </el-form-item>
    <el-form-item>
      <el-input v-model="webinar.link" type="text" />
    </el-form-item>
    <el-form-item>
      <el-button @click="saveWebinar" type="primary">Save</el-button>
      <el-button @click="deleteWebinar" type="danger">Delete</el-button>
    </el-form-item>
    <h4>Participants</h4>
    <el-table :data="webinar.chatters">
      <el-table-column prop="name" label="Name">
        <template #default="{ row }">
          <router-link :to="{ query: { ...route.query, chatter: row.id } }" @click.stop>{{
            row.name
          }}</router-link>
        </template>
      </el-table-column>
      <el-table-column prop="language" label="Language" />
      <el-table-column label="Notification">
        <template #default="{ row }">
          <span style="white-space: nowrap">
            <el-icon>
              <Close
                color="#f56c6c"
                v-if="
                  webinar.webinar_notifications.some(
                    (item) => item.chatter_id === row.id && item.is_failed,
                  )
                "
              />
              <Check
                color="#67c23a"
                v-else-if="
                  webinar.webinar_notifications.some(
                    (item) => item.chatter_id === row.id && item.is_sent,
                  )
                "
              />
              <Timer v-else />
            </el-icon>
            {{
              formatToHumanReadable(
                webinar.webinar_notifications.find((item) => item.chatter_id === row.id)?.send_at,
              )
            }}
          </span>
        </template>
      </el-table-column>
    </el-table>

    <el-form style="margin-top: 20px">
      <el-form-item label="Отправлено автоматических уведомлений">
        {{ notificationsStats.sent }} / {{ notificationsStats.total }}
        <span v-if="notificationsStats.failed" style="color: #f56c6c"
          >{{ notificationsStats.failed }} не дошло</span
        >
      </el-form-item>
      <el-form-item label="Отправить участникам" label-position="top">
        <el-input v-model="message" type="textarea" />
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="sendMessage">Отправить</el-button>
      </el-form-item>
    </el-form>
  </el-card>
</template>
